import {Injectable} from '@angular/core';

import {DatePipe} from '@angular/common';
import {Format} from '../enums/format.enum';
import * as FileSaver from 'file-saver';
import * as jsPDF from 'jspdf';
import 'jspdf-autotable';
import {flatten} from 'flat';
import {DateService, TableColumn} from "@app/shared";

@Injectable()
export class UtilityService {

  // standard format and validation
  Format: typeof Format = Format;

  constructor(private datepipe: DatePipe, private dateService: DateService) {
  }


  /**
   * @desc nullify all input object's properties
   * @param exceptionList Labels of the properties to be exempted from nullifying
   */
  nullifyObjectContent(inputObject, exceptionsList?: string[]) {
    if (!exceptionsList) {
      for (let property in inputObject) {
        inputObject[property] = null;
      }
    } else {
      for (let property in inputObject) {
        if (exceptionsList.indexOf(property) > -1) {
        } else {
          inputObject[property] = null;
        }
      }
    }

  }

  /**
   * @desc add UTC timezone when converting a string date to Date; otherwise dependes on the timezone user is in, the returned Date may be different
   e.g. new Date("2018-01-01") may return "Mon Jan 01 2018 00:00:00 GMT-0400 (EDT)" or "Mon Jan 01 2018 00:00:00 GMT-0500 (EST)" instead of
   "Mon Jan 01 2018 20:00:00 GMT-0400 (EDT)" or "Mon Jan 01 2018 19:00:00 GMT-0500 (EST)"
   * @param date - date in string the format e.g "2018-01-01"
   * @returns: true - Date in local timezone
   */
  convertStringDateToUTCDate(date: string): Date {

    return new Date(date + "T00:00:00Z");
  }

  /**
   * @desc add UTC timzone when converting a string year to Date; otherwise dependes on the timezone user is in, the returned Date may be different
   e.g. new Date("2018-01-01") may return "Mon Jan 01 2018 00:00:00 GMT-0400 (EDT)" or "Mon Jan 01 2018 00:00:00 GMT-0500 (EST)" instead of
   "Mon Jan 01 2018 20:00:00 GMT-0400 (EDT)" or "Mon Jan 01 2018 19:00:00 GMT-0500 (EST)"
   * @param date - year in string the format e.g "2018"
   * @returns: true - Date in local timezone
   */
  convertStringYearToUTCDate(year: string): Date {

    return new Date(year + "-01-01T00:00:00Z");
  }

  /**
   * @desc returns date with timezone offset added and dayOffset applied
   * @param date - date in either string ("2018-01-01") or local Date
   * @returns: true - if MM-dd-YYYY representation of two dates are equal, other wise returns false
   */
  dateWithOffset(date: string | Date, dayOffset?: number): Date {

    let value: Date = (date instanceof Date) ? date as Date : new Date(date);
    dayOffset = dayOffset || 0;

    // 86400000 mili-seconds in a day, 60000 milli-seconds in a minute
    // add dayOffset and timezone offset
    return new Date(value.getTime() + 86400000 * dayOffset + value.getTimezoneOffset() * 60000);
  }

  /**
   * @desc compares two input javascript dates and returns true if their date MM-dd-YYYY value in the spcified timezone are same.
   * @param dateTimie1, dateTime2
   * @returns: true - if MM-dd-YYYY representation of two dates are equal, other wise returns false
   */
  areDatesEqual(dateTime1: Date, dateTime2: Date, timeZone?: string): boolean {
    let date1, date2: string;

    // Angular 5 required to support TimeZone
    if (timeZone) {
      date1 = this.datepipe.transform(dateTime1, Format.TimesheetDate, timeZone);
      date2 = this.datepipe.transform(dateTime2, Format.TimesheetDate, timeZone);
    } else {
      date1 = this.datepipe.transform(dateTime1, Format.TimesheetDate);
      date2 = this.datepipe.transform(dateTime2, Format.TimesheetDate);
    }

    return (date1 === date2);
  }

  isStringDateInRange(from: string, thru: string, date: string) {
    const fromDate = this.convertStringDateToUTCDate(from);
    const thruDate = this.convertStringDateToUTCDate(thru);
    const dateDate = this.convertStringDateToUTCDate(date);

    return (dateDate >= fromDate && dateDate <= thruDate);
  }

  rangeToString(fromDate: Date, thruDate?: Date): string {
    let range: string;
    if (fromDate) {
      range = `${fromDate.getFullYear()}.${fromDate.getMonth()}.${fromDate.getDate()}`;
      if (thruDate) {
        range += `-${thruDate.getFullYear()}.${thruDate.getMonth()}.${thruDate.getDate()}`;
      }
    }
    return range;
  }

  /**
   * @desc return range in string = from - thru
   * @param from in "YYYY/MM/DD"
   * @param thru in "YYYY/MM/DD"
   */
  toRange(from: string, thru?: string): string {
    return from + "-" + thru;
  }

  buildHtmlImageSrc(contentType: string, data: string): string {
    return 'data:' + contentType + ';base64,' + data;
  }

  arrayToParamString(paramName: string, array: Array<string | number>): string {
    let params = "";
    array.forEach(elem => {
      params += `&${paramName}=${elem}`;
    });
    return params;
  }

  exportPdf(objectArray: any[], exportColumns: any[], filename?: string) {
    const doc = new jsPDF.default("l", "pt");
    (doc as any).autoTable({
      body: objectArray,
      theme: 'plain',
      didDrawCell: data => {
      }
    });
    doc.save(filename);
  }

  /**
   * export the input objectArray to an Excel spreadsheet
   * @param objectArray - array of objects to be exported
   * @param columns - fields/columns to export
   * @param fileName - optional Excel filename
   */
  exportExcel(objectArray: Array<any>, columns?: string[], fileName?: string, headerNames?: TableColumn[]) {

    // flatten the object array in order to export e.g.
    // [ {accountNumber: 1000, budget: {initialBudget: 10, targetBudget: 20} }, {...} ] =>
    // [ {accountNumber: 1000, budget.initialBudget: 10, budget.targetBudget: 20} }, {...} ]

    let flattenObjectArray = [];
    objectArray.forEach(obj => flattenObjectArray.push(flatten(obj)));

    // remove other than the input fields/columns from the object array
    if (columns && columns.length > 0) {
      flattenObjectArray.forEach(obj => {
        Object.keys(obj).forEach(key => {
          if (!columns.includes(key)) {
            delete obj[key];
          }
        });
      });
    }


    // convert the object to excel worksheet while retaining the column names
    void import("xlsx").then(xlsx => {
      let worksheet;
      if(headerNames && headerNames.length > 0){

        // Working decent
        // flattenObjectArray.forEach(obj => {
        //   let innerObject:Map<string, any> = new Map<string, any>();
        //   Object.keys(obj).forEach(key => {
        //     innerObject.set(key,obj[key]);
        //     fieldSet.add(key);
        //   });
        //   finalSortedArray[flattenObjectArray.indexOf(obj)] = innerObject;
        // });


        // // Not working
        // flattenObjectArray.forEach(obj => {
        //   let innerObject = {};
        //   let objectOrder = {};
        //   headerNames.forEach(h => { objectOrder[h.field] = null})
        //   let newObjectInOrder = Object.assign(objectOrder, obj);
        //   Object.keys(obj).sort((a, b) => {
        //     return columns.indexOf(a) - columns.indexOf(b);
        //   }).forEach(key => {
        //     fieldSet.add(key);
        //   });
        //   finalSortedArray[flattenObjectArray.indexOf(obj)] = innerObject;
        //   testFinalSortedArray[flattenObjectArray.indexOf(obj)] = objectOrder;
        // });

        let finalHeaders: string[] = [];
        let finalFields: string[] = [];
        headerNames.forEach(f => {finalHeaders.push(f.header); finalFields.push(f.field); });
        // let transposedArray = [];
        // flattenObjectArray.forEach(arr => {
        //   let innerObject;
        //   Object.keys(arr).map(key => {
        //     let ret=arr.map(r=>r[key]); ret.unshift(key);
        //     innerObject = ret;
        //
        //   });
        //   transposedArray[flattenObjectArray.indexOf(arr)] = innerObject;
        // })
        //
        // const transp = a => Object.keys(a[0]).map(c=>{
        //   let ret=a.map(r=>r[c]); ret.unshift(c);
        //   return ret; });

        console.log(flattenObjectArray);



        const wb = xlsx.utils.book_new();
        const ws = xlsx.utils.json_to_sheet([]);
        // Add the headers, instead of default behaviour of object keys
        xlsx.utils.sheet_add_aoa(ws, [finalHeaders]);
        // Add the content starting from next row
        xlsx.utils.sheet_add_json(ws, flattenObjectArray, { origin: 'A2', header: finalFields, skipHeader: true });

        const transposeSheet = xlsx.utils.json_to_sheet([]);
        xlsx.utils.sheet_set_array_formula(transposeSheet, "A1", "TRANSPOSE(data!A1:AW90)");


        xlsx.utils.book_append_sheet(wb, ws, 'data');
        xlsx.utils.book_append_sheet(wb, transposeSheet, 'summarySheet');
        xlsx.writeFile(wb, 'filename.xlsx');
        const excelBuffer: any = xlsx.write(wb, {bookType: 'xlsx', type: 'array'});
        this.saveAsExcelFile(excelBuffer, fileName);
      } else {
        worksheet = xlsx.utils.json_to_sheet(flattenObjectArray);
        const workbook = {Sheets: {'data': worksheet}, SheetNames: ['data']};
        const excelBuffer: any = xlsx.write(workbook, {bookType: 'xlsx', type: 'array'});
        this.saveAsExcelFile(excelBuffer, fileName);
      }


    });
  }


  saveAsExcelFile(buffer: any, fileName: string): void {
    let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    let EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    const date = this.dateService.convertDateToStringDate(new Date(), Format.ExportDate);
    FileSaver.saveAs(data, fileName + '(Export-' + date.toString() + ')' + EXCEL_EXTENSION);
  }
}
